import axios from 'axios';
import type { EnrollmentFormData } from '../types/enrollment.types';

// Get base API URL - VITE_USER_SERVICE_URL may include /api/v1/user path
const getUserServiceBaseUrl = () => {
  const userUrl = import.meta.env.VITE_USER_SERVICE_URL;
  if (userUrl) {
    // If it already ends with /api/v1/user, strip it to get base URL
    if (userUrl.endsWith('/api/v1/user')) {
      return userUrl.replace('/api/v1/user', '');
    }
    return userUrl;
  }
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3105';
};

const API_BASE_URL = getUserServiceBaseUrl();

// Session token for authenticated enrollment
let kioskSessionToken: string | null = null;

export function setKioskSessionToken(token: string | null) {
  kioskSessionToken = token;
}

export function getKioskSessionToken(): string | null {
  return kioskSessionToken;
}

export interface EnrollmentResponse {
  success: boolean;
  memberId: string;
  message?: string;
}

export interface MemberBasicInfo {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  memberSince?: string;
}

class EnrollmentService {
  /**
   * Check if a phone number is already registered
   */
  async checkPhoneAvailability(phone: string): Promise<{ available: boolean }> {
    try {
      const response = await axios.get<{ available: boolean }>(
        `${API_BASE_URL}/api/v1/user/check-phone/${encodeURIComponent(phone)}`
      );
      return response.data;
    } catch (error) {
      console.error('Phone availability check failed:', error);
      // On error, assume available to avoid blocking enrollment
      return { available: true };
    }
  }

  /**
   * Check if an email is already registered
   */
  async checkEmailAvailability(email: string): Promise<{ available: boolean }> {
    try {
      const response = await axios.get<{ available: boolean }>(
        `${API_BASE_URL}/api/v1/user/check-email/${encodeURIComponent(email)}`
      );
      return response.data;
    } catch (error) {
      console.error('Email availability check failed:', error);
      // On error, assume available to avoid blocking enrollment
      return { available: true };
    }
  }

  /**
   * Get basic info for an existing member by phone (for duplicate member dialog)
   * Requires kiosk session token for security
   */
  async getMemberByPhone(phone: string): Promise<MemberBasicInfo | null> {
    try {
      const headers: Record<string, string> = {};
      if (kioskSessionToken) {
        headers['X-Kiosk-Session-Token'] = kioskSessionToken;
      }

      const response = await axios.get<MemberBasicInfo | null>(
        `${API_BASE_URL}/api/v1/user/lookup-by-phone/${encodeURIComponent(phone)}`,
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error('Member lookup failed:', error);
      return null;
    }
  }

  async enrollMember(data: EnrollmentFormData): Promise<EnrollmentResponse> {
    // Build health questionnaire object
    const healthQuestionnaire = {
      // Basic identifiers
      hkIdPrefix: data.hkIdPrefix || undefined,
      occupation: data.occupation || undefined,

      // Body metrics
      bodyMetrics: data.heightValue || data.weightValue ? {
        heightValue: data.heightValue ? parseFloat(data.heightValue) : undefined,
        heightUnit: data.heightUnit,
        weightValue: data.weightValue ? parseFloat(data.weightValue) : undefined,
        weightUnit: data.weightUnit,
      } : undefined,

      // Chief complaint
      symptomOnset: data.symptomOnsetYear && data.symptomOnsetMonth ? {
        year: parseInt(data.symptomOnsetYear),
        month: parseInt(data.symptomOnsetMonth),
      } : undefined,
      chiefComplaint: data.chiefComplaint || undefined,
      mainProblemToResolve: data.mainProblemToResolve || undefined,

      // Medical conditions
      medicalConditions: data.medicalConditions.length > 0 ? data.medicalConditions : [],

      // Medical history
      medicalHistory: [
        data.surgeryHistory && { type: 'surgery', hasHistory: true, details: data.surgeryDetails || undefined },
        data.injuryHistory && { type: 'injury', hasHistory: true, details: data.injuryDetails || undefined },
        data.accidentHistory && { type: 'accident', hasHistory: true, details: data.accidentDetails || undefined },
        data.implantHistory && { type: 'implant', hasHistory: true, details: data.implantDetails || undefined },
      ].filter(Boolean),

      // Imaging history
      imagingHistory: (data.imagingXray || data.imagingCt || data.imagingMri || data.imagingElectrotherapy || data.imagingMagneticTherapy || data.previousTreatment) ? {
        xray: data.imagingXray,
        ctScan: data.imagingCt,
        mri: data.imagingMri,
        electrotherapy: data.imagingElectrotherapy,
        magneticTherapy: data.imagingMagneticTherapy,
        previousTreatment: data.previousTreatment || undefined,
        treatmentEffectiveness: data.treatmentEffectiveness || undefined,
      } : undefined,

      // Pain assessment
      painLocations: data.painLocations.length > 0 ? data.painLocations : [],
      painCharacteristics: (data.painTypes.length > 0 || data.painTriggers.length > 0 || data.painSeverity > 0 || data.painFrequency) ? {
        types: data.painTypes,
        triggers: data.painTriggers,
        severity: data.painSeverity > 0 ? data.painSeverity : undefined,
        frequency: data.painFrequency || undefined,
      } : undefined,

      // Symptom checklists
      symptomChecklists: [
        data.neckSymptoms.length > 0 && { category: 'neck', symptoms: data.neckSymptoms },
        data.shoulderSymptoms.length > 0 && { category: 'shoulder', symptoms: data.shoulderSymptoms },
        data.spineSymptoms.length > 0 && { category: 'spine', symptoms: data.spineSymptoms },
        data.lumbarSymptoms.length > 0 && { category: 'lumbar', symptoms: data.lumbarSymptoms },
        data.kneeSymptoms.length > 0 && { category: 'knee', symptoms: data.kneeSymptoms },
        data.jointSymptoms.length > 0 && { category: 'joints', symptoms: data.jointSymptoms },
        data.otherSymptoms.length > 0 && { category: 'other', symptoms: data.otherSymptoms },
      ].filter(Boolean),

      // Lifestyle - only include if user provided any lifestyle data
      lifestyle: (data.smoking || data.alcohol || data.workPosture.length > 0 ||
                  data.sleepQuality || data.activityLevel ||
                  data.prolongedSitting || data.prolongedStanding) ? {
        smoking: data.smoking || undefined,
        alcohol: data.alcohol || undefined,
        workPosture: data.workPosture.length > 0 ? data.workPosture : undefined,
        stressLevel: data.stressLevel !== 5 ? data.stressLevel : undefined, // 5 is default, skip if unchanged
        sleepQuality: data.sleepQuality || undefined,
        activityLevel: data.activityLevel || undefined,
        prolongedSitting: data.prolongedSitting || undefined,
        prolongedStanding: data.prolongedStanding || undefined,
      } : undefined,

      // Women's health (only if gender is Female)
      womensHealth: data.gender === 'Female' ? {
        isPregnant: data.isPregnant,
        isPostpartum: data.isPostpartum,
        menstrualRegularity: data.menstrualRegularity || undefined,
        menstrualPain: data.menstrualPain,
        gynecologicalConditions: data.gynecologicalConditions || undefined,
      } : undefined,

      // Medications and allergies
      medicationsAndAllergies: (data.currentMedications || data.supplements || data.medicineAllergies || data.foodAllergies || data.environmentalAllergies) ? {
        currentMedications: data.currentMedications || undefined,
        supplements: data.supplements || undefined,
        medicineAllergies: data.medicineAllergies || undefined,
        foodAllergies: data.foodAllergies || undefined,
        environmentalAllergies: data.environmentalAllergies || undefined,
      } : undefined,

      // Treatment preferences
      treatmentPreferences: (data.expectedOutcomes || data.preferredTherapies.length > 0 || data.refusedTreatments) ? {
        expectedOutcomes: data.expectedOutcomes || undefined,
        preferredTherapies: data.preferredTherapies,
        refusedTreatments: data.refusedTreatments || undefined,
      } : undefined,
    };

    // Transform data for the API
    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email?.toLowerCase().trim() || undefined,
      phoneNumbers: [{
        number: data.phone,
        type: 'MOBILE',
        isPrimary: true,
      }],
      dateOfBirth: new Date(data.dateOfBirth),
      gender: data.gender,
      emergencyContact: (data.emergencyName || data.emergencyPhone) ? {
        name: data.emergencyName || undefined,
        phone: data.emergencyPhone || undefined,
        relationship: data.emergencyRelationship || undefined,
      } : undefined,
      referralSource: data.referralSource,
      referredBy: data.referredBy || undefined,
      referredByPhone: data.referredByPhone || undefined,
      healthQuestionnaire,
      consents: [
        {
          type: 'terms_of_service',
          granted: data.consentTerms,
          timestamp: new Date(),
        },
        {
          type: 'data_privacy',
          granted: data.consentPrivacy,
          timestamp: new Date(),
        },
        {
          type: 'health_disclaimer',
          granted: data.consentHealth,
          timestamp: new Date(),
        },
        {
          type: 'marketing',
          granted: data.consentMarketing,
          timestamp: new Date(),
        },
      ],
      // Only include signature if not skipping
      enrollmentSignature: data.skipSignature ? undefined : {
        signatureData: data.signatureData,
        signedAt: new Date(),
        deviceInfo: navigator.userAgent,
      },
      // Flag for staff enrolling on behalf of client
      skipSignature: data.skipSignature || false,
      enrollmentDate: new Date(),
      registrationSource: 'kiosk',
      membershipStatus: 'active',
      memberTier: 'standard',
    };

    try {
      // Build headers with session token
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (kioskSessionToken) {
        headers['X-Kiosk-Session-Token'] = kioskSessionToken;
      } else {
        console.warn('No kiosk session token available for enrollment');
      }

      const response = await axios.post<{ data: EnrollmentResponse; statusCode: number; message: string }>(
        `${API_BASE_URL}/api/v1/user/enroll`,
        payload,
        { headers }
      );

      // API wraps response in { data, statusCode, message } - extract the inner data
      return response.data.data;
    } catch (error: any) {
      // Check for auth errors
      if (error.response?.status === 401) {
        throw new Error('Session expired. Please close this window and start a new enrollment session.');
      }

      // Check for validation errors
      if (error.response?.status === 400) {
        const message = error.response?.data?.message || 'Invalid enrollment data';
        throw new Error(message);
      }

      // Check for server errors
      if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again in a moment.');
      }

      // Network or other errors
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        throw new Error('Unable to connect to the server. Please check your connection.');
      }

      throw error;
    }
  }
}

export const enrollmentService = new EnrollmentService();
